/* global peopleTabPreference peopleTabPreferenceUrl peopleCanViewPayRates context_event */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import installPeopleControlStore, { COUNT_MODE_FTE, COUNT_MODE_HEADCOUNT } from 'Components/people/peopleControlStore';
import PeoplePositionTypeFilter from 'Components/people/PeoplePositionTypeFilter';
import { POSITION_TYPE_ORDER } from 'Components/people/positionTypes';
import _ from 'lodash';

import SegmentedControl from 'CommonComponents/SegmentedControl';
import SelectField from 'CommonComponents/SelectField';
import Tooltip from 'CommonComponents/Tooltip';

import LockClosedIcon from 'Assets/icons/lock-closed.svg';

/** Matches the debounce used by the workforce expenditure table's search. */
const PERSIST_DEBOUNCE_MS = 400;

const PREFERENCE_MODULE = 'people_tab';

/** `compare_to_version_id: null` means "no comparison" — the current Latest Estimate. */
const COMPARE_TO_LATEST = '';

const SUB_VIEW_BY_PANEL_ID = {
    'people-dashboard': 'dashboard',
    positions: 'positions',
    'organisational-chart': 'organisational-chart',
};

/**
 * Which sub-view is on screen right now.
 *
 * The rail deliberately does not own the sub-view — the sub-view tabs are Blade +
 * Flowbite and whoever owns them owns that preference key. But the rail writes
 * the whole preference value on every change, so it has to read the live sub-view
 * at send time; using the value captured at mount would silently reset the user's
 * sub-view every time they touched a filter.
 */
function currentSubView(fallback) {
    const selectedTab = document.querySelector('#people-cluster-content [role="tab"][aria-selected="true"]');
    const target = selectedTab?.getAttribute('data-tabs-target') || selectedTab?.getAttribute('aria-controls') || '';
    const subView = SUB_VIEW_BY_PANEL_ID[target.replace(/^#/, '')];

    return subView || fallback;
}

/** Server-resolved preference → control state. Every key is guaranteed present. */
function initialControlState(preference) {
    const types = Array.isArray(preference.position_types) ? POSITION_TYPE_ORDER.filter((type) => preference.position_types.includes(type)) : [];

    return {
        count_mode: preference.count_mode === COUNT_MODE_FTE ? COUNT_MODE_FTE : COUNT_MODE_HEADCOUNT,
        // A stored empty array would blank every sub-view at once, so fall back
        // to all types rather than trusting it.
        position_types: types.length ? types : [...POSITION_TYPE_ORDER],
        compare_to_version_id: preference.compare_to_version_id ?? null,
    };
}

/**
 * Normalise `window.peopleCompareVersions` into select options.
 *
 * The version list is not a Blade global yet. Until it is, the control reads this
 * optional global and renders disabled when it is missing — it never invents an
 * endpoint to fetch versions from.
 */
function compareVersionOptions(versions) {
    if (!Array.isArray(versions)) {
        return [];
    }

    return versions
        .filter((version) => version && (version.id ?? version.value) != null)
        .map((version) => ({
            value: String(version.id ?? version.value),
            label: version.name ?? version.label ?? String(version.id ?? version.value),
        }));
}

export default function PeopleControlRail({ preference, preferenceUrl, canViewPayRates, compareVersions }) {
    const [controls, setControls] = useState(() => initialControlState(preference));

    const store = useRef(installPeopleControlStore(controls));
    const isFirstRun = useRef(true);

    const persist = useRef(
        _.debounce((url, value) => {
            axios
                // `event_id` is a sibling of module/value, not part of the stored
                // preference — the server reads it only to keep the org chart's
                // OWN `organigram_filter` preference from diverging from the
                // rail's (see UserPreferenceController::store()). Optional on
                // the server, sent whenever `context_event` is available so the
                // dual-write actually fires from this page.
                .post(url, { module: PREFERENCE_MODULE, value, event_id: context_event?.id ?? null })
                .catch((error) => {
                    // A failed preference write must not break the page — the
                    // controls stay applied for this session either way.
                    console.warn('[PeopleControlRail] Failed to persist people tab preference', error);
                });
        }, PERSIST_DEBOUNCE_MS),
    );

    useEffect(() => {
        return () => persist.current.cancel();
    }, []);

    useEffect(() => {
        store.current.setState(controls);

        // Never persist the state the server just handed us.
        if (isFirstRun.current) {
            isFirstRun.current = false;

            return;
        }

        persist.current(preferenceUrl, {
            sub_view: currentSubView(preference.sub_view),
            count_mode: controls.count_mode,
            position_types: controls.position_types,
            compare_to_version_id: controls.compare_to_version_id,
        });
    }, [controls]);

    /*
     * Nothing else subscribes to a sub-nav click — the controls effect above
     * only fires when a CONTROL changes, so switching sub-view alone (no
     * filter touched) never wrote `sub_view` and the "last sub-view is
     * remembered per user" requirement silently failed on a page that only
     * ever changed tabs. The clicked button names its own target directly, so
     * this does not need to wait for Flowbite's own handler to update
     * aria-selected first.
     */
    useEffect(() => {
        const nav = document.getElementById('people-cluster-nav');

        if (!nav) {
            return undefined;
        }

        function handleTabClick(event) {
            const tabButton = event.target.closest('[role="tab"]');

            if (!tabButton) {
                return;
            }

            const target = (tabButton.getAttribute('data-tabs-target') || tabButton.getAttribute('aria-controls') || '').replace(/^#/, '');
            const subView = SUB_VIEW_BY_PANEL_ID[target];

            if (!subView) {
                return;
            }

            persist.current(preferenceUrl, {
                sub_view: subView,
                count_mode: controls.count_mode,
                position_types: controls.position_types,
                compare_to_version_id: controls.compare_to_version_id,
            });
        }

        nav.addEventListener('click', handleTabClick);

        return () => nav.removeEventListener('click', handleTabClick);
    }, [controls, preferenceUrl]);

    function update(patch) {
        setControls((previous) => ({ ...previous, ...patch }));
    }

    const versionOptions = compareVersionOptions(compareVersions);
    const hasVersions = versionOptions.length > 0;
    const compareOptions = [{ value: COMPARE_TO_LATEST, label: $t('Latest Estimate (no comparison)') }, ...versionOptions];

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-6 tw-gap-y-3 tw-px-4 tw-py-3 tw-mb-4 tw-bg-white tw-rounded-lg tw-shadow-primary">
            <PeoplePositionTypeFilter value={controls.position_types} onChange={(position_types) => update({ position_types })} />

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-3">
                <SegmentedControl
                    options={[
                        { value: COUNT_MODE_HEADCOUNT, label: $t('Headcount') },
                        { value: COUNT_MODE_FTE, label: $t('FTE') },
                    ]}
                    value={controls.count_mode}
                    onChange={(count_mode) => update({ count_mode })}
                />

                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <span className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Compare to')}</span>
                    <CompareToVersionSelect
                        options={compareOptions}
                        value={controls.compare_to_version_id == null ? COMPARE_TO_LATEST : String(controls.compare_to_version_id)}
                        disabled={!hasVersions}
                        onChange={(value) => update({ compare_to_version_id: value === COMPARE_TO_LATEST ? null : value })}
                    />
                </div>

                {!canViewPayRates && <RateLockNotice />}
            </div>
        </div>
    );
}

function CompareToVersionSelect({ options, value, disabled, onChange }) {
    const select = (
        <div className="tw-w-56">
            <SelectField options={options} value={value} isDisabled={disabled} onChange={(option) => onChange(option ? option.value : COMPARE_TO_LATEST)} />
        </div>
    );

    if (!disabled) {
        return select;
    }

    return (
        <Tooltip tooltipType="text" tooltipContent={$t('This budget has no other versions to compare against yet.')} placement="bottom">
            {select}
        </Tooltip>
    );
}

/**
 * Pay-rate lock. The payload OMITS money and per-person keys for a user without
 * the permission, so the affordance explains the absence instead of standing in
 * for a value — nothing anywhere renders a placeholder figure.
 */
function RateLockNotice() {
    return (
        <Tooltip
            tooltipType="text"
            tooltipContent={$t('You do not have permission to view pay rates, so rate and per-person figures are hidden.')}
            placement="bottom"
        >
            <span className="tw-inline-flex tw-items-center tw-gap-x-1.5 tw-px-2 tw-py-1 tw-rounded tw-bg-charcoal-10 tw-text-xs tw-font-medium tw-text-charcoal-75 tw-cursor-help">
                <LockClosedIcon className="tw-w-3.5 tw-h-3.5" />
                {$t('Rates hidden')}
            </span>
        </Tooltip>
    );
}

const railEl = document.getElementById('PeopleControlRail');

if (railEl) {
    ReactDOM.render(
        <App>
            <PeopleControlRail
                preference={peopleTabPreference}
                preferenceUrl={peopleTabPreferenceUrl}
                canViewPayRates={peopleCanViewPayRates}
                compareVersions={typeof window.peopleCompareVersions !== 'undefined' ? window.peopleCompareVersions : null}
            />
        </App>,
        railEl,
    );
}
